import { useState, useEffect } from "react";
import { SidebarTrigger } from "@components/ui/sidebar";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { TeacherBalanceTopbar } from "./teacher-balance-topbar.content";
import { UnpaidLessonsDialogContainer } from "@invoicing-context/primary/teacher/unpaid-lessons/unpaid-lessons-dialog.container";
import { teacherRetrievePayoutRequestEligibility } from "@invoicing-context/read/application/use-cases/teacher/payout-request-eligibility/retrieve-payout-request-eligibility";
import { selectTeacherPayoutEligibilityRetrieval } from "@invoicing-context/read/application/use-cases/teacher/payout-request-eligibility/selectors/payout-eligibility-selectors";
import { selectTeacherProfileRetrieval } from "@user-management-context/read/application/use-cases/teacher/teacher-profile-retrieval/selectors/teacher-profile-selectors";
import { TEACHER_PAYOUT_MODE } from "@user-management-context/read/domain/types/admin/teacher";
import { formatToCurrency } from "@utils/formatting";

const TeacherBalanceTopbarSkeleton = () => {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/80 p-2 backdrop-blur-sm">
      <SidebarTrigger />
      <Skeleton className="h-4 w-64" />
      <div className="ml-auto">
        <Skeleton className="h-9 w-36" />
      </div>
    </div>
  );
};

export const TeacherBalanceTopbarContainer = () => {
  const dispatch = useAppDispatch();
  const { data, fetching } = useAppSelector(selectTeacherPayoutEligibilityRetrieval);
  const { data: teacher } = useAppSelector(selectTeacherProfileRetrieval);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Only fetch when state is idle to prevent re-fetching on status changes (idle → pending → success)
  useEffect(() => {
    if (fetching === "idle") {
      dispatch(teacherRetrievePayoutRequestEligibility());
    }
  }, [dispatch, fetching]);

  if (fetching === "pending" || fetching === "idle") {
    return <TeacherBalanceTopbarSkeleton />;
  }

  const { isEligible, summary } = data;
  const isMonthlyPayment = teacher?.payoutMode === TEACHER_PAYOUT_MODE.END_OF_MONTH;
  const hasUnpaidLessons = isEligible && summary.totalHours > 0;

  if (isMonthlyPayment) {
    const hasHours = summary.totalHours > 0;
    return (
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/80 p-2 py-3 backdrop-blur-sm">
        <SidebarTrigger />
        <div className="mx-auto flex items-center justify-center">
          {hasHours ? (
            <p className="text-xs text-muted-foreground lg:text-sm">
              <strong>{summary.totalHours}</strong> heure(s) (<strong>{formatToCurrency(summary.totalAmount)}</strong>) seront payées
              automatiquement le 1er du mois
            </p>
          ) : (
            <span className="text-sm text-muted-foreground">Paiement automatique le 1er du mois</span>
          )}
        </div>
      </div>
    );
  }

  if (!hasUnpaidLessons) {
    return (
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/80 p-2 py-3 backdrop-blur-sm">
        <SidebarTrigger />
        <div className="mx-auto flex items-center justify-center">
          <span className="text-sm text-muted-foreground">Aucun cours en attente de règlement</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="sticky top-0 z-10 flex cursor-pointer items-center gap-2 border-b bg-background/80 p-2 py-3 backdrop-blur-sm transition-colors hover:bg-amber-50 dark:hover:bg-amber-950/50"
        onClick={() => setDialogOpen(true)}
      >
        <SidebarTrigger onClick={e => e.stopPropagation()} className="hover:bg-accent" />
        <TeacherBalanceTopbar
          totalHours={summary.totalHours}
          totalAmount={summary.totalAmount}
          lastPayoutDate={summary.lastPayoutDate}
          onRequestPayout={() => setDialogOpen(true)}
        />
      </div>
      <UnpaidLessonsDialogContainer open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
};
